"use client";

import {
    type MutableRefObject,
    type ReactNode,
    useLayoutEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { type Layout, useGroupRef } from "react-resizable-panels";

import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ColumnsBlock } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import {
    columnMin,
    columnShares,
    wholePercents,
    withWidths,
} from "../../../lib/columnWidths";
import NestedBlocks from "./nestedBlocks";
import { AlignedColumn } from "./columnAlign";

type Drag = { widths: number[]; top: number } | null;

const createDragStore = () => {
    let drag: Drag = null;
    const listeners = new Set<() => void>();
    return {
        get: () => drag,
        set: (next: Drag) => {
            drag = next;
            listeners.forEach((l) => l());
        },
        subscribe: (l: () => void) => {
            listeners.add(l);
            return () => {
                listeners.delete(l);
            };
        },
    };
};
type DragStore = ReturnType<typeof createDragStore>;

const RESIZE_KEYS = new Set([
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
]);

/**
 * The editor's resizable row. The panels always show the saved widths: only a
 * drag or a keyboard resize of a divider writes, so opening a lomda, undo and
 * redo never save anything.
 */
export default function ResizableColumns({ block }: { block: ColumnsBlock }) {
    const { columns } = block.data;
    const refocus = useRef<number | null>(null);
    // Panel sizes are read at mount only, so the row mounts again when the
    // column count or a column's minimum (under 10 for old data) changes.
    const key = [columns.length, ...columnShares(columns).map(columnMin)];
    return <ColumnsRow key={key.join()} block={block} refocus={refocus} />;
}

function ColumnsRow({
    block,
    refocus,
}: {
    block: ColumnsBlock;
    refocus: MutableRefObject<number | null>;
}) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const setActiveSlot = useLomdaV3Editor((s) => s.setActiveSlot);
    const { columns } = block.data;
    const shares = columnShares(columns);
    const groupRef = useGroupRef();
    const rowRef = useRef<HTMLDivElement>(null);
    const syncing = useRef(false);
    const pressed = useRef<{ top: number } | null>(null);
    const keyboard = useRef(false);
    const [drag] = useState(createDragStore);
    // A changed size prop re-registers the panel, and the library then
    // re-fits the row on its own.
    const [mountShares] = useState(
        () => new Map(columns.map((c, i) => [c.id, shares[i]]))
    );

    const syncToData = () => {
        const group = groupRef.current;
        if (!group) return;
        const layout = group.getLayout();
        if (
            Object.keys(layout).length !== columns.length ||
            columns.some((c) => layout[c.id] === undefined) ||
            columns.every((c, i) => Math.abs(layout[c.id] - shares[i]) < 0.1)
        )
            return;
        const target = Object.fromEntries(
            columns.map((c, i) => [c.id, shares[i]])
        );
        syncing.current = true;
        group.setLayout(
            Object.fromEntries(
                Object.keys(layout).map((id) => [id, target[id]])
            )
        );
        syncing.current = false;
    };
    useLayoutEffect(syncToData);

    // After a remount the keyboard stays on the same divider.
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) return;
        const dividers = () =>
            Array.from(
                row.querySelectorAll<HTMLElement>(":scope > [data-separator]")
            );
        if (refocus.current !== null) dividers()[refocus.current]?.focus();
        refocus.current = null;
        return () => {
            const i = dividers().findIndex((d) => d === document.activeElement);
            refocus.current = i >= 0 ? i : null;
        };
    }, [refocus]);

    const onLayoutChange = (layout: Layout) => {
        if (pressed.current && !syncing.current)
            drag.set({
                widths: columns.map((c) => layout[c.id] ?? 0),
                top: pressed.current.top,
            });
    };
    const onLayoutChanged = (layout: Layout) => {
        if (syncing.current) return;
        if (columns.some((c) => layout[c.id] === undefined)) return;
        if (!pressed.current && !keyboard.current) return syncToData();
        const next = wholePercents(columns.map((c) => layout[c.id]));
        const saved = wholePercents(shares);
        const changed = withWidths(columns, next);
        if (!changed || next.every((w, i) => w === saved[i]))
            return syncToData();
        replaceBlockData(block.id, { ...block.data, columns: changed });
    };

    const endPress = () => {
        window.removeEventListener("pointerup", endPress);
        window.removeEventListener("pointercancel", endPress);
        // After the library's own pointerup, which reports the final layout.
        setTimeout(() => {
            pressed.current = null;
            drag.set(null);
        });
    };

    const children: ReactNode[] = [];
    columns.forEach((col, i) => {
        if (i > 0) {
            children.push(
                <ResizableHandle
                    key={`handle-${col.id}`}
                    withHandle
                    floatingHandle
                    disableDoubleClick
                    // h-auto: the row has no set height, so h-full was 0px.
                    className="mx-1.5 h-auto w-0.5 rounded bg-border transition-colors hover:bg-[hsl(var(--platform-primary))] data-[separator=active]:bg-[hsl(var(--platform-primary))]"
                />
            );
        }
        const mountShare = mountShares.get(col.id) ?? shares[i];
        children.push(
            <ResizablePanel
                key={col.id}
                id={col.id}
                defaultSize={`${mountShare}%`}
                minSize={`${columnMin(mountShare)}%`}
                onPointerDownCapture={() =>
                    setActiveSlot({ containerId: block.id, slotId: col.id })
                }
                className="relative min-w-0 rounded-xl border border-dashed border-border/70 p-2"
            >
                <WidthChip store={drag} index={i} />
                <AlignedColumn align={col.verticalAlign}>
                    <NestedBlocks
                        containerId={block.id}
                        slotId={col.id}
                        blocks={col.blocks}
                        editable
                    />
                </AlignedColumn>
            </ResizablePanel>
        );
    });

    return (
        <ResizablePanelGroup
            groupRef={groupRef}
            elementRef={rowRef}
            direction="horizontal"
            className="min-h-[7rem] w-full items-stretch"
            onLayoutChange={onLayoutChange}
            onLayoutChanged={onLayoutChanged}
            onPointerDownCapture={(e) => {
                if (e.button !== 0) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const top = e.clientY - rect.top;
                pressed.current = {
                    top: Math.min(Math.max(top, 14), rect.height - 14),
                };
                window.addEventListener("pointerup", endPress);
                window.addEventListener("pointercancel", endPress);
            }}
            onKeyDownCapture={(e) => {
                if (
                    !RESIZE_KEYS.has(e.key) ||
                    !(e.target as HTMLElement).closest("[data-separator]")
                )
                    return;
                keyboard.current = true;
                setTimeout(() => {
                    keyboard.current = false;
                });
            }}
        >
            {children}
        </ResizablePanelGroup>
    );
}

function WidthChip({ store, index }: { store: DragStore; index: number }) {
    const drag = useSyncExternalStore(store.subscribe, store.get, store.get);
    if (!drag) return null;
    return (
        <span
            dir="ltr"
            style={{ top: drag.top }}
            className="lomda-v3-chrome pointer-events-none absolute inset-x-0 z-20 mx-auto w-fit -translate-y-1/2 rounded-full bg-[hsl(var(--platform-primary))] px-2 py-0.5 text-xs font-medium tabular-nums text-[hsl(var(--platform-primary-foreground))] shadow-sm"
        >
            {wholePercents(drag.widths)[index]}%
        </span>
    );
}
